import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Check, Bone } from "lucide-react";
import { adminApi } from "../../lib/adminAuth";
import { api } from "../../lib/api";

const empty = {
  venue_id: "",
  title: "",
  description: "",
  discount: "",
  terms: "",
  valid_until: "",
  active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [venues, setVenues] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {} or coupon
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.listCoupons().then(setCoupons).catch(() => {});
    api.listVenues().then(setVenues).catch(() => {});
  };
  useEffect(load, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = {
        ...editing,
        valid_until: editing.valid_until || null,
      };
      if (editing.id) {
        await adminApi.updateCoupon(editing.id, payload);
      } else {
        await adminApi.createCoupon(payload);
      }
      setEditing(null);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Error guardando Hueso");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar Hueso "${c.title}"?`)) return;
    await adminApi.deleteCoupon(c.id);
    load();
  };

  return (
    <div data-testid="admin-huesos-page" className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Catálogo</div>
          <h1 className="font-display text-4xl uppercase tracking-tight">Huesos</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{coupons.length} Huesos registrados.</p>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="btn-brutal bg-[#FEBA01] text-black font-display uppercase px-4 py-2 inline-flex items-center gap-2 shadow-brutal-white"
          data-testid="admin-hueso-new-btn"
        >
          <Plus size={16} /> Nuevo Hueso
        </button>
      </header>

      {coupons.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-10 text-center" data-testid="admin-huesos-empty">
          <Bone size={32} strokeWidth={2.2} className="mx-auto text-[#FEBA01] mb-3" />
          <div className="font-display text-xl uppercase">Sin Huesos todavía</div>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-md mx-auto">
            Cuando tengas un acuerdo con un local, crea acá un Hueso. Los usuarios podrán reclamarlo y el
            local lo valida con el código KIL-XXXXXXXX.
          </p>
        </div>
      ) : (
        <div className="bg-[#1A1A1A] border-2 border-[#333] divide-y divide-[#333]">
          {coupons.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-hueso-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase truncate">{c.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-0.5">
                  {c.venue_name} · <span className="text-[#FEBA01]">{c.discount}</span> · {c.active ? "ACTIVO" : "PAUSADO"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setEditing({ ...c, valid_until: c.valid_until || "" })}
                  className="border-2 border-[#FEBA01] text-[#FEBA01] hover:bg-[#FEBA01] hover:text-black p-2"
                  data-testid={`admin-hueso-edit-${c.id}`}
                  aria-label="Editar"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => remove(c)}
                  className="border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white p-2"
                  data-testid={`admin-hueso-delete-${c.id}`}
                  aria-label="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Editar Hueso" : "Nuevo Hueso"} testid="admin-hueso-modal">
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Local *">
              <select
                required
                value={editing.venue_id}
                onChange={(e) => setEditing({ ...editing, venue_id: e.target.value })}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2"
                data-testid="admin-coupon-venue-select"
              >
                <option value="">Seleccionar local…</option>
                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Título *">
              <input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2" data-testid="admin-coupon-title" />
            </Field>
            <Field label="Descripción">
              <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2" data-testid="admin-coupon-description" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Recompensa *">
                <input required value={editing.discount} onChange={(e) => setEditing({ ...editing, discount: e.target.value })} placeholder="20% / 2x1 / Combo" className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2" data-testid="admin-hueso-discount" />
              </Field>
              <Field label="Vence (YYYY-MM-DD)">
                <input value={editing.valid_until || ""} onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} placeholder="2026-12-31" className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2" data-testid="admin-coupon-valid-until" />
              </Field>
            </div>
            <Field label="Términos">
              <textarea rows={2} value={editing.terms || ""} onChange={(e) => setEditing({ ...editing, terms: e.target.value })} className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2" data-testid="admin-coupon-terms" />
            </Field>
            <label className="flex items-center gap-2 font-mono text-xs uppercase text-[#A3A3A3]">
              <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="admin-coupon-active" />
              Activo
            </label>
            {err && <div className="text-sm text-red-400">{err}</div>}
            <div className="flex gap-2 pt-2">
              <button type="submit" disabled={busy} className="btn-brutal bg-[#FEBA01] text-black font-display uppercase px-4 py-2 shadow-brutal-white disabled:opacity-50 inline-flex items-center gap-2" data-testid="admin-coupon-save">
                <Check size={16} /> {busy ? "Guardando…" : "Guardar"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="border-2 border-[#333] text-[#A3A3A3] hover:text-white px-4 py-2 font-display uppercase">
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">{label}</span>
      {children}
    </label>
  );
}

export function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div className="bg-[#1A1A1A] border-2 border-[#FEBA01] w-full max-w-lg shadow-brutal my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#333]">
          <div className="font-display text-xl uppercase">{title}</div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-[#FEBA01]" aria-label="Cerrar"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
